/**
 * AI Skill Service
 *
 * Purpose: API client for AI Skills Manager CRUD. Skills are GLOBAL-managed
 *          name + markdown records surfaced in the AI Agent Manager skills
 *          dropdown (ORCH-205).
 * Architecture: Thin wrapper over apiService; mirrors aiAgentService shape.
 * Author: Development Team
 * Created: 2026-06-10
 */

import { apiService } from './api';

// =============================================================================
// TYPES
// =============================================================================

export interface UserInfo {
  email: string;
  user_id: string;
  first_name?: string;
  last_name?: string;
}

export interface AiSkill {
  id: string;
  name: string;
  md_content: string;
  tool_calls: string[];
  created_at: string;
  updated_at: string;
  created_by?: string | null;
  updated_by?: string | null;
  created_by_user?: UserInfo;
  updated_by_user?: UserInfo;
}

export interface AiSkillCreatePayload {
  name: string;
  md_content?: string;
  tool_calls?: string[];
}

export interface AiSkillUpdatePayload {
  name?: string;
  md_content?: string;
  tool_calls?: string[];
}

export interface AiSkillOptions {
  tool_calls: string[];
}

export interface AiSkillListParams {
  name_contains?: string;
}

/** Lightweight {id, name} projection used by the Agent Manager skills dropdown. */
export interface AiSkillOption {
  id: string;
  name: string;
}

export interface AiSkillBlockingAgent {
  ai_agent_id: string;
  tenant_id: string;
  label: string;
}

export interface AiSkillConflict {
  error: 'duplicate_name' | 'skill_in_use';
  message: string;
  conflicting_record?: { id: string; name: string };
  blocking_agents?: AiSkillBlockingAgent[];
}

// =============================================================================
// SERVICE
// =============================================================================

class AiSkillServiceImpl {
  private baseUrl = '/metadata/ai-skills';

  async listAISkills(params: AiSkillListParams = {}): Promise<AiSkill[]> {
    const response = await apiService.api.get<AiSkill[]>(this.baseUrl, { params });
    return response.data;
  }

  async getAISkill(skillId: string): Promise<AiSkill> {
    const response = await apiService.api.get<AiSkill>(`${this.baseUrl}/${skillId}`);
    return response.data;
  }

  async createAISkill(data: AiSkillCreatePayload): Promise<AiSkill> {
    const response = await apiService.api.post<AiSkill>(this.baseUrl, data);
    return response.data;
  }

  async updateAISkill(skillId: string, data: AiSkillUpdatePayload): Promise<AiSkill> {
    const response = await apiService.api.put<AiSkill>(`${this.baseUrl}/${skillId}`, data);
    return response.data;
  }

  async deleteAISkill(skillId: string): Promise<void> {
    await apiService.api.delete(`${this.baseUrl}/${skillId}`);
  }

  async getAISkillOptions(): Promise<AiSkillOptions> {
    const response = await apiService.api.get<AiSkillOptions>(`${this.baseUrl}/options`);
    return response.data;
  }

  /**
   * Extract a structured 409 detail (duplicate-name or skill-in-use) from an
   * Axios error. Returns null when the error is not a recognized 409 shape.
   */
  extractConflict(error: unknown): AiSkillConflict | null {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const err = error as any;
    if (!err?.response || err.response.status !== 409) {
      return null;
    }
    const detail = err.response.data?.detail ?? err.response.data;
    if (detail && typeof detail === 'object' && 'error' in detail) {
      return detail as AiSkillConflict;
    }
    return null;
  }
}

export const aiSkillService = new AiSkillServiceImpl();
export default aiSkillService;
